import { type ComunicazioneDTO } from '@framework/api-client/api/backoffice/schemas/comunicazioneDTO.ts';
import { ButtonIcon } from '@framework/ui/components/button/ButtonIcon.tsx';
import { IconWrapper } from '@framework/ui/components/icon/IconWrapper.tsx';
import { type ButtonProps, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { TbArrowForwardUp } from 'react-icons/tb';
import { useCommunicationsContext } from '../../context/CommunicationsContext.tsx';
import { getCommunicationForwardText } from '../../form/getCommunicationForwardText.tsx';

interface IForwardCommunicationButtonProps extends ButtonProps {
  communication: ComunicazioneDTO;
  isDraft: boolean | null | undefined;
  isBackoffice: boolean | null | undefined;
  onEdit: ((record: any) => void) | undefined;
}

export function ForwardCommunicationButton(
  props: IForwardCommunicationButtonProps,
) {
  const { communication, onEdit, isDraft, isBackoffice, ...buttonProps } =
    props;

  const { t } = useTranslation();

  const { editableTypes } = useCommunicationsContext();

  if (Boolean(isDraft) || !isBackoffice) {
    return null;
  }

  // Se non ci sono comunicazioni "modificabili" (creazione) non mostriamo il pulsante
  // (non possiamo tecnicamente creare e inoltrare nessun tipo comunicazione, quindi nemmeno creare un inoltro)
  if (!editableTypes.length) {
    return null;
  }

  if (process.env.DISABLE_COMMUNICATION_FORWARD === 'true') {
    return null;
  }

  return (
    <ButtonIcon
      aria-label={t('communication-item.forward', 'Inoltra')}
      icon={
        <IconWrapper
          aria-label={t('communication-item.forward', 'Inoltra')}
          icon={<TbArrowForwardUp aria-hidden="true" />}
        />
      }
      tooltip={t('communication-item.forward', 'Inoltra')}
      onClick={e => {
        if (!communication.id) {
          // biome-ignore format: <tbd>
          message.error(t('communication-item.forward.error', 'Errore: impossibile inoltrare la comunicazione'));
          return;
        }
        onEdit?.({
          inoltro: {
            idComunicazione: communication.id,
            allegati: communication.allegati?.map(allegato => ({
              idAllegato: allegato.id ?? '<unknown>',
              allegato,
            })),
            messaggio: getCommunicationForwardText(communication),
          },
        });
        // Evitiamo che il click venga propagato
        // così che in visualizzazione standard non si apra/chiuda la riga
        e.stopPropagation();
        e.preventDefault();
      }}
      {...buttonProps}
    />
  );
}
